import { Link } from "react-router-dom";
import { ArrowRight, Scale, Stethoscope, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/journey/TopBar";
import FacultyDialog from "@/components/journey/FacultyDialog";
import LegalAdvisorDialog from "@/components/journey/LegalAdvisorDialog";
import Tm from "@/components/journey/Tm";

function LanguageGate({ soundRef }) {
  const { update } = useApp();
  const choose = (lang) => {
    soundRef.current?.play();
    update({ lang });
  };
  return (
    <section className="glass mx-auto max-w-lg rounded-[2rem] p-8 text-center sm:p-12" data-testid="language-gate">
      <div className="mb-6 flex justify-center"><Logo size="h-28 w-28" /></div>
      <h1 className="font-display text-4xl font-semibold tracking-[0.12em] text-white sm:text-5xl">JOYFUL MINDS<Tm sizeEm={0.55} /></h1>
      <p className="mt-3 text-sm text-emerald-100/80">Choose your language · ഭാഷ തിരഞ്ഞെടുക്കുക</p>
      <div className="mt-8 grid grid-cols-2 gap-3">
        <Button data-testid="choose-language-en" onClick={() => choose("en")} className="h-14 rounded-2xl bg-emerald-400 text-base text-emerald-950 hover:bg-emerald-300">
          English
        </Button>
        <Button data-testid="choose-language-ml" onClick={() => choose("ml")} className="h-14 rounded-2xl bg-amber-300 text-base text-emerald-950 hover:bg-amber-200" style={{ fontFamily: "Noto Sans Malayalam" }}>
          മലയാളം
        </Button>
      </div>
    </section>
  );
}

export default function StepWelcome({ soundRef }) {
  const { state, t, update, lang, settings, pick } = useApp();
  const quote = pick(settings.texts, "quote");
  const quoteRef = pick(settings.texts, "quote_ref");
  if (!state.lang) return <LanguageGate soundRef={soundRef} />;
  return (
    <section className="glass rounded-[2rem] p-8 sm:p-12" data-testid="step-welcome">
      <div className="mb-5"><Logo size="h-24 w-24" /></div>
      <p className={`text-xs uppercase text-amber-200/90 ${lang === "en" ? "tracking-[0.3em]" : "text-sm"}`}>{t("brandSub")}</p>
      <h1 className="mt-3 font-display text-4xl font-semibold tracking-[0.1em] text-white sm:text-5xl lg:text-6xl" data-testid="site-title">
        JOYFUL MINDS<Tm sizeEm={0.5} />
      </h1>
      <p className="mt-3 max-w-md text-base text-emerald-50/80 md:text-lg">{t("brandTag")}</p>
      {quote && (
        <blockquote className="mt-5 border-l-2 border-amber-200/50 pl-4" data-testid="welcome-quote">
          <p className="font-display text-lg italic text-amber-100">"{quote}"</p>
          {quoteRef && <cite className="mt-1 block text-xs not-italic tracking-wide text-emerald-100/60">{quoteRef}</cite>}
        </blockquote>
      )}
      <div className="mt-8 flex flex-col items-start gap-2" data-testid="welcome-primary-cta">
        <Button
          data-testid="book-appointment-top-btn"
          onClick={() => update({ step: 2 })}
          className="h-12 w-full rounded-full bg-emerald-400 px-4 text-sm font-semibold text-emerald-950 transition-transform hover:-translate-y-0.5 hover:bg-emerald-300 sm:text-base"
        >
          {t("proceedBooking")} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Link
          to="/edit-booking"
          data-testid="edit-booking-top-link"
          className="text-sm text-emerald-100/80 underline underline-offset-4 hover:text-emerald-50"
        >
          {t("editMyBooking")}
        </Link>
      </div>
      <div className="mt-6 grid gap-3">
        <FacultyDialog
          trigger={
            <button type="button" data-testid="faculty-btn" className="glass-soft group flex items-center gap-3 rounded-2xl p-4 text-left transition-colors hover:bg-white/10">
              <Stethoscope className="h-5 w-5 text-emerald-300" />
              <span className="flex-1 text-sm text-emerald-50">{t("faculty")} · {t("knowDoctor")}</span>
              <ArrowRight className="h-4 w-4 text-emerald-200/50 transition-transform group-hover:translate-x-1" />
            </button>
          }
        />
        <Link to="/forum" data-testid="open-forum-link" className="glass-soft group flex items-center gap-3 rounded-2xl p-4 text-left transition-colors hover:bg-white/10">
          <Users className="h-5 w-5 shrink-0 text-amber-200" />
          <span className="flex-1 text-sm text-emerald-50">Join <b>JOYFUL MINDS&trade;</b> Mental Health Forum</span>
          <ArrowRight className="h-4 w-4 text-emerald-200/50 transition-transform group-hover:translate-x-1" />
        </Link>
        <LegalAdvisorDialog
          trigger={
            <button type="button" data-testid="legal-advisor-btn" className="glass-soft group flex items-center gap-3 rounded-2xl p-4 text-left transition-colors hover:bg-white/10">
              <Scale className="h-5 w-5 shrink-0 text-amber-200" />
              <span className="flex-1 text-sm text-emerald-50">{t("knowLegalAdvisor")}</span>
              <ArrowRight className="h-4 w-4 text-emerald-200/50 transition-transform group-hover:translate-x-1" />
            </button>
          }
        />
      </div>
      <div className="mt-10 flex flex-col items-start gap-3">
        <Link to="/admin" data-testid="admin-link" className="text-xs text-emerald-100/40 hover:text-emerald-100/80">
          {t("admin")}
        </Link>
      </div>
    </section>
  );
}
